'use client';

import { useEffect, useMemo, useState } from 'react';
import { useOrderStore } from '@/store/useOrderStore';
import { calculateMultiItemDiscounts, calculatePrice, calcScheduleCharges, calcLocationAdjustments, calcDistanceSurchargeFromRoad } from '@/lib/price-calculator';
import type { LocationType } from '@/types/order';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  adminMode?: boolean;
}

export default function OrderSummary({ adminMode = false }: Props) {
  const {
    itemInfo,
    items,
    origin,
    destination,
    priceBreakdown,
    scheduleDate,
    designatedPickupSlot,
    designatedDeliverySlot,
    flexiblePickupMorning,
    flexiblePickupAfternoon,
    pickupDiscountRate,
    deliveryDiscountRate,
    quoteExtraItem,
    setDistanceKm,
    setPriceBreakdown,
  } = useOrderStore();

  const [manualDistance, setManualDistance] = useState<string>('');
  const [manualOriginFloor, setManualOriginFloor] = useState<string>('');
  const [manualDestFloor, setManualDestFloor] = useState<string>('');
  const [manualOriginLocationType, setManualOriginLocationType] = useState<LocationType | null>(null);
  const [manualDestLocationType, setManualDestLocationType] = useState<LocationType | null>(null);

  // 모든 물품 = 확정된 items + 현재 편집 중인 itemInfo (있으면)
  const allItems = useMemo(
    () => [
      ...items.map((i) => ({
        uid: i.uid,
        displayName: i.displayName,
        basePrice: i.basePrice,
        locationInsideExtra: i.locationInsideExtra ?? 0,
        locationParkingDiscount: i.locationParkingDiscount ?? 0,
      })),
      ...(itemInfo.itemId
        ? [{
            uid: '_current',
            displayName: itemInfo.category || '물품',
            basePrice: itemInfo.basePrice,
            locationInsideExtra: itemInfo.locationInsideExtra ?? 0,
            locationParkingDiscount: itemInfo.locationParkingDiscount ?? 0,
          }]
        : []),
    ],
    [items, itemInfo.itemId, itemInfo.category, itemInfo.basePrice, itemInfo.locationInsideExtra, itemInfo.locationParkingDiscount],
  );

  // 다물품 할인 계산
  const discountMap = useMemo(() => calculateMultiItemDiscounts(allItems), [allItems]);

  // allItems(확정+편집중) 전체에 다물품 할인 적용한 합산가 — 관리자 임시 계산도 이 값을 사용
  const itemBasePriceTotal = useMemo(() => {
    if (allItems.length === 0) return 0;
    return allItems.reduce((sum, i) => {
      const d = discountMap.get(i.uid);
      return sum + (d ? d.discountedPrice : i.basePrice);
    }, 0);
  }, [allItems, discountMap]);

  const parseManualFloor = (raw: string): number => {
    if (raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const recomputeManualPrice = (
    distanceStr: string,
    originFloorStr: string,
    destFloorStr: string,
    originLocType: LocationType | null,
    destLocType: LocationType | null,
  ) => {
    const roadKm = Number(distanceStr);
    if (!itemBasePriceTotal || isNaN(roadKm) || distanceStr === '') {
      return;
    }
    const originFloor = parseManualFloor(originFloorStr);
    const destFloor = parseManualFloor(destFloorStr);
    const locationAdjustments = calcLocationAdjustments(
      allItems.map((i) => ({
        uid: i.uid,
        basePrice: i.basePrice,
        locationInsideExtra: i.locationInsideExtra,
        locationParkingDiscount: i.locationParkingDiscount,
      })),
      originLocType,
      destLocType,
    );
    // 관리자 임시 입력: 도로거리를 직접 받아 거리할증 적용 (× 1.3 보정 없음)
    const distanceSurchargeOverride = calcDistanceSurchargeFromRoad(roadKm);
    const breakdown = calculatePrice({
      itemBasePrice: itemBasePriceTotal,
      straightKm: 0, // override 사용 시 의미 없음
      originHasElevator: false,
      originFloor,
      destHasElevator: false,
      destFloor,
      locationAdjustments,
      distanceSurchargeOverride,
    });
    setDistanceKm(roadKm);
    setPriceBreakdown(breakdown);
  };

  // 관리자 임시 입력 사용 중이면 물품 추가/제거·위치 선택에도 가격이 즉시 따라오도록 재계산
  useEffect(() => {
    if (!adminMode) return;
    if (manualDistance === '') return;
    recomputeManualPrice(manualDistance, manualOriginFloor, manualDestFloor, manualOriginLocationType, manualDestLocationType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemBasePriceTotal, adminMode, manualOriginLocationType, manualDestLocationType]);

  const flexCount =
    (flexiblePickupMorning ? 1 : 0) +
    (flexiblePickupAfternoon ? 1 : 0);
  const hasDesignatedDelivery = !!designatedDeliverySlot;

  // 일정 할인/할증 통합 계산 (저가 물품 규칙 포함)
  const charges = priceBreakdown
    ? calcScheduleCharges({
        baseTotal: priceBreakdown.totalPrice,
        itemBasePrice: priceBreakdown.itemBasePrice,
        flexCount,
        hasDesignatedPickup: !!designatedPickupSlot,
        hasDesignatedDelivery,
        pickupDiscountRate,
        deliveryDiscountRate,
      })
    : null;

  const formatPrice = (price: number) => price.toLocaleString() + '원';

  const getScheduleLabel = () => {
    const parts: string[] = [];
    if (designatedPickupSlot) parts.push(`수거 ${designatedPickupSlot}`);
    if (designatedDeliverySlot) parts.push(`배송 ${designatedDeliverySlot}`);
    return parts.length > 0 ? parts.join(' / ') : null;
  };

  return (
    <Card className="sticky top-20 border-gray-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold text-[#1A3A5C]">주문 요약</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 물품 정보 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">선택 물품</div>
          {allItems.length > 0 ? (
            <div className="space-y-1">
              {allItems.map((item) => {
                const disc = discountMap.get(item.uid);
                const hasDiscount = disc && disc.discountRate > 0;
                return (
                  <div key={item.uid} className="flex justify-between items-center">
                    <span className="font-medium text-gray-900 text-sm">{item.displayName}</span>
                    {hasDiscount ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 line-through">{formatPrice(item.basePrice)}</span>
                        <span className="text-xs bg-red-100 text-red-600 px-1 py-0.5 rounded-full font-medium">
                          {Math.round(disc.discountRate * 100)}%
                        </span>
                        <span className="text-sm text-[#4DA3FF]">{formatPrice(disc.discountedPrice)}</span>
                      </span>
                    ) : (
                      <span className="text-sm text-[#4DA3FF]">{formatPrice(item.basePrice)}</span>
                    )}
                  </div>
                );
              })}
              {allItems.length >= 2 && (
                <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                  <span className="text-xs text-gray-500">물품 소계</span>
                  <span className="text-sm font-medium text-[#4DA3FF]">
                    {formatPrice(
                      allItems.reduce((sum, i) => {
                        const d = discountMap.get(i.uid);
                        return sum + (d ? d.discountedPrice : i.basePrice);
                      }, 0),
                    )}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="font-medium text-gray-400">
              {itemInfo.isCustom ? '커스텀 물품 (견적 요청)' : '물품을 선택해주세요'}
            </div>
          )}
        </div>

        <Separator />

        {/* 경로 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">배송 경로</div>
          {origin && destination ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-700 truncate">
                  {origin.address.split(' ').slice(1, 3).join(' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-sm text-gray-700 truncate">
                  {destination.address.split(' ').slice(1, 3).join(' ')}
                </span>
              </div>
            </div>
          ) : (
            <div className="font-medium text-gray-400">주소를 입력해주세요</div>
          )}
        </div>

        <Separator />

        {/* 관리자 임시 입력 */}
        {adminMode && (
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded p-2 space-y-2">
            <div className="text-xs font-medium text-gray-700">관리자 임시 입력</div>
            <div className="space-y-1">
              <Label htmlFor="manual-distance" className="text-xs text-gray-600">도로거리 (km)</Label>
              <Input
                id="manual-distance"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.1}
                placeholder="0"
                value={manualDistance}
                onChange={(e) => {
                  setManualDistance(e.target.value);
                  recomputeManualPrice(e.target.value, manualOriginFloor, manualDestFloor, manualOriginLocationType, manualDestLocationType);
                }}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <div className="text-xs text-gray-600">수거 위치 (출발지)</div>
              <div className="flex gap-1">
                {(['INSIDE', 'DOOR', 'PARKING'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setManualOriginLocationType((prev) => (prev === t ? null : t))}
                    className={cn(
                      'flex-1 py-1 rounded text-[11px] font-medium border',
                      manualOriginLocationType === t
                        ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#1A3A5C]',
                    )}
                  >
                    {t === 'INSIDE' ? '집안' : t === 'DOOR' ? '문앞' : '주차장'}
                  </button>
                ))}
              </div>
              {manualOriginLocationType !== 'PARKING' && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor="manual-origin-floor" className="text-xs text-gray-600">계단 운반 층수 (출발지)</Label>
                  <Input
                    id="manual-origin-floor"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={manualOriginFloor}
                    onChange={(e) => {
                      setManualOriginFloor(e.target.value);
                      recomputeManualPrice(manualDistance, e.target.value, manualDestFloor, manualOriginLocationType, manualDestLocationType);
                    }}
                    className="h-9"
                  />
                </div>
              )}
            </div>
            <div className="space-y-1">
              <div className="text-xs text-gray-600">배송 위치 (도착지)</div>
              <div className="flex gap-1">
                {(['INSIDE', 'DOOR', 'PARKING'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setManualDestLocationType((prev) => (prev === t ? null : t))}
                    className={cn(
                      'flex-1 py-1 rounded text-[11px] font-medium border',
                      manualDestLocationType === t
                        ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#1A3A5C]',
                    )}
                  >
                    {t === 'INSIDE' ? '집안' : t === 'DOOR' ? '문앞' : '주차장'}
                  </button>
                ))}
              </div>
              {manualDestLocationType !== 'PARKING' && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor="manual-dest-floor" className="text-xs text-gray-600">계단 운반 층수 (도착지)</Label>
                  <Input
                    id="manual-dest-floor"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={manualDestFloor}
                    onChange={(e) => {
                      setManualDestFloor(e.target.value);
                      recomputeManualPrice(manualDistance, manualOriginFloor, e.target.value, manualOriginLocationType, manualDestLocationType);
                    }}
                    className="h-9"
                  />
                </div>
              )}
            </div>
            <p className="text-[10px] text-gray-500">실제 주소 입력 시 덮어씌워집니다.</p>
          </div>
        )}

        {/* 운송비 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">예상 운송비</div>
          {priceBreakdown ? (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">물품 운송비</span>
                <span>{formatPrice(priceBreakdown.itemBasePrice)}</span>
              </div>
              {priceBreakdown.distanceSurcharge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">거리할증</span>
                  <span>+{formatPrice(priceBreakdown.distanceSurcharge)}</span>
                </div>
              )}
              {priceBreakdown.stairSurchargeOrigin > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">계단할증 (출발지)</span>
                  <span>+{formatPrice(priceBreakdown.stairSurchargeOrigin)}</span>
                </div>
              )}
              {priceBreakdown.stairSurchargeDest > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">계단할증 (도착지)</span>
                  <span>+{formatPrice(priceBreakdown.stairSurchargeDest)}</span>
                </div>
              )}
              {priceBreakdown.locationOriginSurcharge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">집안 수거 추가</span>
                  <span>+{formatPrice(priceBreakdown.locationOriginSurcharge)}</span>
                </div>
              )}
              {priceBreakdown.locationOriginDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>주차장 수거 할인</span>
                  <span>-{formatPrice(priceBreakdown.locationOriginDiscount)}</span>
                </div>
              )}
              {priceBreakdown.locationDestSurcharge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">집안 배송 추가</span>
                  <span>+{formatPrice(priceBreakdown.locationDestSurcharge)}</span>
                </div>
              )}
              {priceBreakdown.locationDestDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>주차장 배송 할인</span>
                  <span>-{formatPrice(priceBreakdown.locationDestDiscount)}</span>
                </div>
              )}
              {(charges?.flexDiscount ?? 0) > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>유연시간 할인</span>
                  <span>-{formatPrice(charges!.flexDiscount)}</span>
                </div>
              )}
              {(charges?.slotDiscount ?? 0) > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>최적시간 할인</span>
                  <span>-{formatPrice(charges!.slotDiscount)}</span>
                </div>
              )}
              {(charges?.designatedPickupSurcharge ?? 0) > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>수거시간 지정 할증</span>
                  <span>+{formatPrice(charges!.designatedPickupSurcharge)}</span>
                </div>
              )}
              {(charges?.singleFlexSurcharge ?? 0) > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>유연시간 1개 할증</span>
                  <span>+{formatPrice(charges!.singleFlexSurcharge)}</span>
                </div>
              )}
              {(charges?.deliverySurcharge ?? 0) > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>도착시간 지정</span>
                  <span>+{formatPrice(charges!.deliverySurcharge)}</span>
                </div>
              )}
              {charges && (quoteExtraItem?.amount ?? 0) !== 0 && quoteExtraItem && (
                <div className={`flex justify-between text-sm ${quoteExtraItem.amount < 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  <span>{quoteExtraItem.name}</span>
                  <span>{quoteExtraItem.amount > 0 ? '+' : '-'}{formatPrice(Math.abs(quoteExtraItem.amount))}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-bold">
                <span className="text-[#1A3A5C]">합계</span>
                <span className="text-[#4DA3FF]">{formatPrice((charges?.finalPrice ?? priceBreakdown.totalPrice) + (charges ? (quoteExtraItem?.amount ?? 0) : 0))}</span>
              </div>
            </div>
          ) : (
            <div className="font-medium text-gray-400">정보 입력 후 계산됩니다</div>
          )}
        </div>

        <Separator />

        {/* 일정 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">배송 일정</div>
          {scheduleDate ? (
            <div className="font-medium text-gray-900">
              {scheduleDate}
              {getScheduleLabel() && (
                <div className="text-sm text-gray-600 mt-0.5">{getScheduleLabel()}</div>
              )}
            </div>
          ) : (
            <div className="font-medium text-gray-400">일정을 선택해주세요</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
